import { clampMoney, roundMoney } from '../money';
import { calculateOrderChargesTotal } from './order-charges.utils';
import type { OrderChargeDraft, OrderSummary, PaymentEntry } from '../../types/sales.types';
import { calculateAdjustedPrice, type LineAdjustmentType } from './line-pricing.utils';

export interface OrderSummaryLine {
  originalPrice: number;
  markupType: LineAdjustmentType;
  markupValue: number;
  adjustedPrice: number;
  discountType: LineAdjustmentType;
  discountValue: number;
  finalPrice: number;
}

/**
 * Markup for summary = Σ(adjusted − catalog).
 * Prefers stored adjustedPrice so the summary matches the line body/total.
 */
export function sumStoredMarkupAmounts(lines: OrderSummaryLine[]): number {
  return roundMoney(
    lines.reduce((total, line) => {
      const original = roundMoney(line.originalPrice);
      const adjusted = resolveAdjustedPrice(line);
      return total + roundMoney(Math.max(0, adjusted - original));
    }, 0),
  );
}

/**
 * Discount for summary = Σ(adjusted − final).
 * Keeps ledger identity: subtotal + markup − discount === productsTotal.
 */
export function sumStoredDiscountAmounts(lines: OrderSummaryLine[]): number {
  return roundMoney(
    lines.reduce((total, line) => {
      const adjusted = resolveAdjustedPrice(line);
      const finalPrice = roundMoney(line.finalPrice);
      return total + roundMoney(Math.max(0, adjusted - finalPrice));
    }, 0),
  );
}

function resolveAdjustedPrice(line: OrderSummaryLine): number {
  if (line.adjustedPrice != null && Number.isFinite(Number(line.adjustedPrice))) {
    return roundMoney(line.adjustedPrice);
  }
  return calculateAdjustedPrice(line.originalPrice, line.markupType, line.markupValue);
}

export function buildOrderSummary(params: {
  lines: OrderSummaryLine[];
  charges?: OrderChargeDraft[];
  payments?: PaymentEntry[];
  allowPartialPayment?: boolean;
}): OrderSummary {
  const { lines, charges = [], payments = [], allowPartialPayment = false } = params;

  const subtotal = roundMoney(
    lines.reduce((total, line) => total + roundMoney(line.originalPrice), 0),
  );
  const totalMarkup = sumStoredMarkupAmounts(lines);
  const totalDiscount = sumStoredDiscountAmounts(lines);
  const productsTotal = roundMoney(
    lines.reduce((total, line) => total + roundMoney(line.finalPrice), 0),
  );
  const totalCharges = calculateOrderChargesTotal(charges);
  const grandTotal = clampMoney(productsTotal + totalCharges);

  const amountPaid = roundMoney(payments.reduce((total, payment) => total + payment.amount, 0));

  const totalCashReceived = roundMoney(
    payments
      .filter((p) => p.method === 'CASH' && p.cashReceived != null)
      .reduce((total, payment) => total + (payment.cashReceived ?? 0), 0),
  );

  const cashPaymentsTotal = roundMoney(
    payments
      .filter((p) => p.method === 'CASH')
      .reduce((total, payment) => total + payment.amount, 0),
  );

  const change = clampMoney(totalCashReceived - cashPaymentsTotal);
  const pendingBalance = clampMoney(grandTotal - amountPaid);

  let paymentStatus: OrderSummary['paymentStatus'] = 'pending';
  if (amountPaid >= grandTotal) {
    paymentStatus = 'complete';
  } else if (amountPaid > 0 && allowPartialPayment) {
    paymentStatus = 'partial';
  }

  return {
    subtotal,
    totalMarkup,
    totalDiscount,
    totalCharges,
    productsTotal: clampMoney(productsTotal),
    grandTotal: clampMoney(grandTotal),
    amountPaid,
    change,
    pendingBalance,
    paymentStatus,
  };
}
